import type { ComponentProps } from 'react';

import { Translate, TranslatePlural } from '@change-corgi/core/react/i18n';
import { VisuallyHidden } from '@change-corgi/design-system/a11y';
import { Button } from '@change-corgi/design-system/components/actions';
import { Separator } from '@change-corgi/design-system/components/content';
import { Loader } from '@change-corgi/design-system/components/progressiveDisclosure';
import { Box } from '@change-corgi/design-system/layout';
import { Text } from '@change-corgi/design-system/typography';

import { useLazyLoadedListFocus } from 'src/app/shared/hooks/focus';

import { CommentCard } from './components/CommentCard';
import { usePageBody } from './hooks/usePageBody';

export function PageBody(props: ComponentProps<typeof Box>) {
	const {
		data: { comments, hasNextPage, nextPageLoading, hasSignedPetition },
		actions: { loadMore },
	} = usePageBody();

	const { containerRef, loadMoreRef, loadingRef, getListItemRef, recordListEvent, lastEvent } =
		useLazyLoadedListFocus();

	if (!comments.length) {
		return (
			<Box {...props}>
				<Separator mb={32} />
				<Text data-qa="comments-empty-list">
					{hasSignedPetition ? (
						<Translate value="fe.components.comments.be_the_first" />
					) : (
						<Translate value="fe.components.comments.sign_this_petition" />
					)}
				</Text>
			</Box>
		);
	}

	return (
		<Box ref={containerRef} {...props}>
			{/* using role due to https://www.scottohara.me/blog/2019/01/12/lists-and-safari.html */}
			<Box as="ul" role="list" data-qa="comments-list">
				{comments.map((comment, index) => (
					<Box as="li" key={comment.id}>
						{lastEvent.type === 'loadMore' && index === lastEvent.firstNewItemIndex && (
							<VisuallyHidden role="alert">
								<TranslatePlural
									value="corgi.petition.comments.fetched_more"
									count={comments.length - lastEvent.previousItemsCount}
									replacements={{ count: comments.length - lastEvent.previousItemsCount }}
								/>
							</VisuallyHidden>
						)}
						<Box mb={24}>
							<CommentCard comment={comment} commentLinkRef={getListItemRef(index)} />
						</Box>
					</Box>
				))}
			</Box>
			{nextPageLoading && (
				<>
					<VisuallyHidden role="alert">
						<Translate value="corgi.petition.comments.fetching_more" />
					</VisuallyHidden>
					{/* 44 is the height of the button */}
					<Loader size={44} mx="auto" ref={loadingRef} mt={24} />
				</>
			)}
			{hasNextPage && !nextPageLoading && (
				<Button
					variant="secondary"
					type="button"
					sx={{ width: '100%' }}
					data-testid="load-more-comments"
					onClick={() => {
						recordListEvent({ type: 'loadMore', itemsCount: comments.length, triggerFocusFallback: true });
						loadMore();
					}}
					ref={loadMoreRef}
				>
					<Translate value="fe.components.load_more.load_more" />
				</Button>
			)}
		</Box>
	);
}
